import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { useAppSelector, useAppDispatch } from '@store';
import {
  useScroll,
  SecurityLocks,
  PickerPosition,
  PermissionEnum,
  PermissionDenied,
} from '@ui/src';
import { clearLocks, setLocksTakeCount } from '../store/slices/security';
import { useLazyGetCustomerLocksQuery } from '../store/services/individual';
import {
  schema,
  columns,
  columnsLabels,
  filtersSchema,
} from './variables/locks';

import cn from '../information.module.css';
import { clearActiveLock } from '../store/slices/general-info';

const IndividualCustomerSecurityLocks = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    individualCustomers: {
      info: {
        general: { baseInformation, activeLock },
        security: { locks, locksTakeCount, pagination },
      },
    },
    userList: {
      users: { users },
    },
    root: { activePermissions },
  } = useAppSelector((state) => state);

  const dispatch = useAppDispatch();

  const [getList, { isFetching, isLoading }] = useLazyGetCustomerLocksQuery();

  const { search } = useLocation();

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (scrollRef && scrollRef.current) {
      const { offsetHeight } = scrollRef.current;

      if (offsetHeight) {
        const take = offsetHeight / 58;

        dispatch(setLocksTakeCount(Math.ceil(take + 3)));
      }
    }
  }, [scrollRef]);

  useEffect(() => {
    if (search && activeLock && locks !== undefined) {
      const parsed = queryString.parse(search);

      if (
        parsed &&
        parsed.locked &&
        parsed.locked === 'true' &&
        !locks?.length
      ) {
        dispatch(clearActiveLock());
      }
    }
  }, [search, locks, activeLock]);

  useEffect(
    () => () => {
      dispatch(clearLocks());
    },
    [],
  );

  let content;

  if (!activePermissions?.includes(PermissionEnum.LOCKS_LIST)) {
    content = <PermissionDenied />;
  } else {
    content = (
      <>
        <SecurityLocks
          locks={locks}
          pagination={pagination}
          takeCount={locksTakeCount}
          users={users}
          schema={schema}
          getList={getList}
          filtersSchema={filtersSchema}
          isFetching={isFetching}
          isLoading={isLoading}
          columns={columns}
          columnsLabels={columnsLabels}
          userId={baseInformation?.userId}
          tableTopClassName={cn.securityTableTop}
          expirationDatesPickerPosition={PickerPosition.RIGHT}
          withPickerTitles
        />
        <Outlet />
      </>
    );
  }

  return (
    <div className={cn.securityLocksContent} ref={scrollRef}>
      {content}
    </div>
  );
};

export default IndividualCustomerSecurityLocks;
